import React, { useCallback, useMemo } from 'react'
import { OptionProps, SingleValueProps, components } from 'react-select'
import { twMerge } from 'tailwind-merge'
import {
  OptionType,
  SelectNew,
} from 'src/editor/shared/ui-lib/select-new/select-new'
import {
  WebsiteThemeListInterface,
  WebsiteThemeListColorsInterface,
} from 'src/editor/store/website-settings/website-settings-types'
import { ColorsPreview } from './colors-preview'
import { CheckIcon } from 'src/editor/shared/icons/check-icon'
import { Trash02Icon } from 'src/editor/shared/icons/trash-02-icon'

interface ThemeSelectProps {
  themes?: WebsiteThemeListInterface[]
  activeThemeId: WebsiteThemeListInterface['id']
  update: (value: WebsiteThemeListInterface['id']) => void
  onDelete: (value: WebsiteThemeListInterface['id']) => void
  isLoading: boolean
}

interface ThemeSelectOptionInterface
  extends OptionType<WebsiteThemeListInterface['id']> {
  colors: WebsiteThemeListColorsInterface
  isCommon: boolean
  onDelete: (id: WebsiteThemeListInterface['id']) => void
}

export const ThemeSelect = ({
  themes = [],
  activeThemeId,
  update,
  onDelete,
  isLoading,
}: ThemeSelectProps) => {
  const options = useMemo<ThemeSelectOptionInterface[]>(
    () =>
      themes.map(theme => ({
        value: theme.id,
        label: theme.name,
        colors: theme.colors,
        isCommon: theme.isCommon,
        onDelete: onDelete,
      })),
    [themes, onDelete],
  )

  const selectedOption =
    options.find(option => option.value === activeThemeId) || null

  const handleChange = useCallback(
    (value: ThemeSelectOptionInterface | null) => {
      if (value) update(value.value)
    },
    [update],
  )

  return (
    <SelectNew<number, false, ThemeSelectOptionInterface>
      isMulti={false}
      selectedOption={selectedOption}
      onChange={handleChange}
      options={options}
      components={{ Option, SingleValue }}
      isLoading={isLoading}
    />
  )
}

const SingleValue = (
  props: SingleValueProps<ThemeSelectOptionInterface, false>,
) => {
  const { data } = props

  return (
    <components.SingleValue
      {...props}
      className={twMerge(props.className, 'flex')}
    >
      <div>{data.label}</div>
      <ColorsPreview colors={data.colors} className="ml-3" />
    </components.SingleValue>
  )
}

const Option = (props: OptionProps<ThemeSelectOptionInterface, false>) => {
  const { isSelected, data } = props
  return (
    <components.Option
      {...props}
      className={twMerge(
        'hover:!bg-bgSecondary !cursor-pointer px-3 py-2 transition-colors',
        isSelected ? '!bg-bgSecondary' : '!bg-bgPrimaryBase',
      )}
    >
      <div
        className={twMerge(
          'flex flex-col gap-1 text-textPrimary',
          isSelected ? 'bg-bgSecondary' : '',
        )}
      >
        <div className="flex items-center justify-between">
          <span className="text-M text-textPrimary">{data.label}</span>
          {isSelected && (
            <CheckIcon
              width={16}
              height={16}
              className="mr-1 ml-1.5 fill-textPrimary"
            />
          )}
        </div>
        <div className="flex h-6 items-center justify-between">
          <ColorsPreview colors={data.colors} />

          {!data.isCommon && (
            <button
              type="button"
              className="flex h-6 w-6 cursor-pointer items-center justify-center text-iconTertiary transition-colors hover:text-iconError"
              onClick={() => data.onDelete(data.value)}
            >
              <Trash02Icon width={16} height={16} />
            </button>
          )}
        </div>
      </div>
    </components.Option>
  )
}
